import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  fetchWishlist as requestFetchWishlist,
  toggleWishlist as requestToggleWishlist,
  clearWishlist as requestClearWishlist,
} from '../../util/api';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';

// ================ Action types ================ //

export const FETCH_WISHLIST_REQUEST = 'app/WishlistPage/FETCH_WISHLIST_REQUEST';
export const FETCH_WISHLIST_SUCCESS = 'app/WishlistPage/FETCH_WISHLIST_SUCCESS';
export const FETCH_WISHLIST_ERROR = 'app/WishlistPage/FETCH_WISHLIST_ERROR';

export const TOGGLE_WISHLIST_REQUEST = 'app/WishlistPage/TOGGLE_WISHLIST_REQUEST';
export const TOGGLE_WISHLIST_SUCCESS = 'app/WishlistPage/TOGGLE_WISHLIST_SUCCESS';
export const TOGGLE_WISHLIST_ERROR = 'app/WishlistPage/TOGGLE_WISHLIST_ERROR';

export const FETCH_WISHLIST_LISTINGS_REQUEST = 'app/WishlistPage/FETCH_WISHLIST_LISTINGS_REQUEST';
export const FETCH_WISHLIST_LISTINGS_SUCCESS = 'app/WishlistPage/FETCH_WISHLIST_LISTINGS_SUCCESS';
export const FETCH_WISHLIST_LISTINGS_ERROR = 'app/WishlistPage/FETCH_WISHLIST_LISTINGS_ERROR';

export const CLEAR_WISHLIST_REQUEST = 'app/WishlistPage/CLEAR_WISHLIST_REQUEST';
export const CLEAR_WISHLIST_SUCCESS = 'app/WishlistPage/CLEAR_WISHLIST_SUCCESS';
export const CLEAR_WISHLIST_ERROR = 'app/WishlistPage/CLEAR_WISHLIST_ERROR';

export const SET_WISHLIST_UNAVAILABLE_LISTINGS_REMOVED =
  'app/WishlistPage/SET_WISHLIST_UNAVAILABLE_LISTINGS_REMOVED';

const RESULT_PAGE_SIZE = 100;

const FETCH_WISHLIST_LISTINGS_RESULT = {
  data: [],
  meta: {
    totalItems: 0,
    totalPages: 0,
    page: 1,
    paginationLimit: 0,
    perPage: RESULT_PAGE_SIZE,
  },
};

// ================ Reducer ================ //

const initialState = {
  wishlist: [],
  currentPageResultIds: [],
  pagination: null,
  fetchWishlistError: null,
  fetchWishlistInProgress: false,
  fetchWishlistSucceed: false,
  toggleWishlistError: null,
  toggleWishlistInProgress: false,
  toggleWishlistSucceed: false,
  fetchWishlistListingsError: null,
  fetchWishlistListingsInProgress: false,
  fetchWishlistListingsSucceed: false,
  clearWishlistError: null,
  clearWishlistInProgress: false,
  clearWishlistSucceed: false,
  wishlistUnavailableListingsRemoved: false,
};

export default function reducer(state = initialState, { type, payload } = {}) {
  switch (type) {
    case FETCH_WISHLIST_REQUEST:
      return {
        ...state,
        fetchWishlistInProgress: true,
        fetchWishlistError: null,
        fetchWishlistSucceed: false,
      };
    case FETCH_WISHLIST_SUCCESS:
      return {
        ...state,
        fetchWishlistInProgress: false,
        fetchWishlistSucceed: true,
        wishlist: payload,
      };
    case FETCH_WISHLIST_ERROR:
      return {
        ...state,
        fetchWishlistInProgress: false,
        fetchWishlistError: payload,
      };
    case TOGGLE_WISHLIST_REQUEST:
      return {
        ...state,
        toggleWishlistInProgress: true,
        toggleWishlistError: null,
        toggleWishlistSucceed: false,
      };
    case TOGGLE_WISHLIST_SUCCESS: {
      const { pagination, currentPageResultIds } = state;

      const nextState = {
        ...state,
        toggleWishlistInProgress: false,
        toggleWishlistSucceed: true,
        wishlist: payload,
        currentPageResultIds: currentPageResultIds.filter(({ uuid }) => payload.indexOf(uuid) >= 0),
      };

      const added = currentPageResultIds.length < nextState.currentPageResultIds.length;
      const removed = currentPageResultIds.length > nextState.currentPageResultIds.length;

      if (!pagination || !(added || removed)) {
        return nextState;
      }

      return {
        ...nextState,
        pagination: {
          ...pagination,
          totalItems: pagination.totalItems + (added ? 1 : -1),
        },
      };
    }
    case TOGGLE_WISHLIST_ERROR:
      return {
        ...state,
        toggleWishlistInProgress: false,
        toggleWishlistError: payload,
      };
    case FETCH_WISHLIST_LISTINGS_REQUEST:
      return {
        ...state,
        fetchWishlistListingsInProgress: true,
        fetchWishlistListingsError: null,
        fetchWishlistListingsSucceed: false,
        currentPageResultIds: [],
      };
    case FETCH_WISHLIST_LISTINGS_SUCCESS:
      return {
        ...state,
        fetchWishlistListingsInProgress: false,
        fetchWishlistListingsSucceed: true,
        pagination: payload.meta,
        currentPageResultIds: payload.data.map(({ id }) => id),
      };
    case FETCH_WISHLIST_LISTINGS_ERROR:
      return {
        ...state,
        fetchWishlistListingsInProgress: false,
        fetchWishlistListingsError: payload,
      };
    case CLEAR_WISHLIST_REQUEST:
      return {
        ...state,
        clearWishlistInProgress: true,
        clearWishlistError: null,
        clearWishlistSucceed: false,
      };
    case CLEAR_WISHLIST_SUCCESS:
      return {
        ...state,
        clearWishlistInProgress: false,
        clearWishlistSucceed: true,
        wishlist: payload,
        pagination: FETCH_WISHLIST_LISTINGS_RESULT.meta,
        currentPageResultIds: [],
      };
    case CLEAR_WISHLIST_ERROR:
      return {
        ...state,
        clearWishlistInProgress: false,
        clearWishlistError: payload,
      };
    case SET_WISHLIST_UNAVAILABLE_LISTINGS_REMOVED:
      return {
        ...state,
        wishlistUnavailableListingsRemoved: payload,
      };
    default:
      return state;
  }
}

// ================ Selectors ================ //

export const wishlistSelector = state => {
  return state.WishlistPage;
};

export const wishlistCountSelector = state => {
  const { wishlist } = wishlistSelector(state);
  return wishlist.length;
};

// ================ Action creators ================ //

export const fetchWishlistRequest = () => ({
  type: FETCH_WISHLIST_REQUEST,
});

export const fetchWishlistSuccess = wishlist => ({
  type: FETCH_WISHLIST_SUCCESS,
  payload: wishlist,
});

export const fetchWishlistError = error => ({
  type: FETCH_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const toggleWishlistRequest = () => ({
  type: TOGGLE_WISHLIST_REQUEST,
});

export const toggleWishlistSuccess = wishlist => ({
  type: TOGGLE_WISHLIST_SUCCESS,
  payload: wishlist,
});

export const toggleWishlistError = error => ({
  type: TOGGLE_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const fetchWishlistListingsRequest = () => ({
  type: FETCH_WISHLIST_LISTINGS_REQUEST,
});

export const fetchWishlistListingsSuccess = response => ({
  type: FETCH_WISHLIST_LISTINGS_SUCCESS,
  payload: response,
});

export const fetchWishlistListingsError = error => ({
  type: FETCH_WISHLIST_LISTINGS_ERROR,
  payload: error,
  error: true,
});

export const clearWishlistRequest = () => ({
  type: CLEAR_WISHLIST_REQUEST,
});

export const clearWishlistSuccess = wishlist => ({
  type: CLEAR_WISHLIST_SUCCESS,
  payload: wishlist,
});

export const clearWishlistError = error => ({
  type: CLEAR_WISHLIST_ERROR,
  payload: error,
  error: true,
});

export const setWishlistUnavailableListingsRemoved = removed => ({
  type: SET_WISHLIST_UNAVAILABLE_LISTINGS_REMOVED,
  payload: removed,
});

// ================ Thunks ================ //

export const fetchWishlist = () => async (dispatch, getState) => {
  const state = wishlistSelector(getState());

  if (state.fetchWishlistInProgress || state.fetchWishlistSucceed || state.fetchWishlistError) {
    return state.wishlist;
  }

  dispatch(fetchWishlistRequest());

  try {
    const response = await requestFetchWishlist();

    dispatch(fetchWishlistSuccess(response.data));

    return response.data;
  } catch (e) {
    dispatch(fetchWishlistError(storableError(e)));
  }
};

export const toggleWishlist = ({ listingId }) => async dispatch => {
  dispatch(toggleWishlistRequest());

  try {
    const response = await requestToggleWishlist({ listingId });
    const wishlist = response.data;

    dispatch(toggleWishlistSuccess(wishlist));

    return wishlist;
  } catch (e) {
    dispatch(toggleWishlistError(storableError(e)));
    throw e;
  }
};

export const removeUnavailableWishlistListings = (wishlist, listings) => async (
  dispatch,
  getState,
  sdk
) => {
  if (wishlist.length === listings.length) {
    dispatch(setWishlistUnavailableListingsRemoved(false));
    return;
  }

  const availableListingIds = listings.map(listing => listing.id.uuid);
  const unavailableListingIds = wishlist.filter(
    listingId => availableListingIds.indexOf(listingId) === -1
  );

  if (!unavailableListingIds.length) {
    dispatch(setWishlistUnavailableListingsRemoved(false));
    return;
  }

  await dispatch(clearWishlist(unavailableListingIds));

  dispatch(setWishlistUnavailableListingsRemoved(true));
};

export const fetchWishlistListings = params => async dispatch => {
  dispatch(fetchWishlistListingsRequest());

  try {
    const wishlist = await dispatch(fetchWishlist());
    const response = await dispatch(requestFetchWishlistListings(wishlist, params));
    await dispatch(removeUnavailableWishlistListings(wishlist, response.data));

    dispatch(fetchWishlistListingsSuccess(response));

    return response;
  } catch (e) {
    dispatch(fetchWishlistListingsError(storableError(e)));
  }
};

export const requestFetchWishlistListings = (wishlist, params) => async (
  dispatch,
  _getState,
  sdk
) => {
  if (!wishlist?.length) {
    return FETCH_WISHLIST_LISTINGS_RESULT;
  }

  const response = await sdk.listings.query({ ids: wishlist, ...params });

  dispatch(addMarketplaceEntities(response));

  return response?.data;
};

export const clearWishlist = listingIds => async (dispatch, getState) => {
  dispatch(clearWishlistRequest());

  try {
    const response = await requestClearWishlist({ listingIds });
    const wishlist = response.data;

    dispatch(clearWishlistSuccess(wishlist));

    return wishlist;
  } catch (e) {
    dispatch(clearWishlistError(storableError(e)));
  }
};

export const loadData = (params, search, config) => {
  const queryParams = parse(search);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return fetchWishlistListings({
    ...queryParams,
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      'variants.square-small2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
    minStock: 1,
    stockMode: 'match-undefined',
  });
};
